'use client';

import { createContext, useContext, ReactNode } from 'react';
import { SpaceRole } from '@/lib/types';

export interface SpaceInfo {
  id: string;
  name: string;
  slug: string;
  description?: string;
  icon?: string;
}

export interface MembershipInfo {
  id: string;
  role: SpaceRole;
  joinedAt: string;
}

export interface SpaceContextValue {
  space: SpaceInfo;
  membership: MembershipInfo;
  isOwner: boolean;
  isManager: boolean;
  canManage: boolean;
}

const SpaceContext = createContext<SpaceContextValue | null>(null);

interface SpaceProviderProps {
  children: ReactNode;
  space: SpaceInfo;
  membership: MembershipInfo;
}

export function SpaceProvider({ children, space, membership }: SpaceProviderProps) {
  const isOwner = membership.role === SpaceRole.Owner;
  const isManager = membership.role === SpaceRole.Manager;
  const canManage = isOwner || isManager;

  return (
    <SpaceContext.Provider
      value={{
        space,
        membership,
        isOwner,
        isManager,
        canManage,
      }}
    >
      {children}
    </SpaceContext.Provider>
  );
}

export function useSpace(): SpaceContextValue {
  const context = useContext(SpaceContext);
  if (!context) {
    throw new Error('useSpace must be used within a SpaceProvider');
  }
  return context;
}

export function useSpaceOptional(): SpaceContextValue | null {
  return useContext(SpaceContext);
}
